import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    Box,
    Typography,
    IconButton,
    Tooltip,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Close as CloseIcon,
    ContentCopy as ContentCopyIcon,
    GridView as GridViewIcon,
    FormatListBulleted as ListIcon
} from '@mui/icons-material';
import { InkColorIcons } from './InkColorIcon';
import DeckVisual from './DeckVisual';

const TYPE_ORDER = { character: 0, item: 1, location: 2, action: 3, song: 3 };

function sortCardsFlat(cards) {
    const hasTypeData = cards.some(c => c.type != null);
    return [...cards].sort((a, b) => {
        if (hasTypeData) {
            const ta = TYPE_ORDER[a.type?.toLowerCase()] ?? 0;
            const tb = TYPE_ORDER[b.type?.toLowerCase()] ?? 0;
            if (ta !== tb) return ta - tb;
        }
        const ca = a.cost ?? 99, cb = b.cost ?? 99;
        if (ca !== cb) return ca - cb;
        return (a.sortNumber || 0) - (b.sortNumber || 0);
    });
}

function DeckCardList({ deck }) {
    const [previewCard, setPreviewCard] = useState(null);
    const cards = Array.isArray(deck?.deckVisual?.cards) ? deck.deckVisual.cards : [];
    const sorted = sortCardsFlat(cards);

    return (
        <Box sx={{ p: { xs: 1, sm: 1.5 } }}>
            {sorted.map((card) => {
                const imageUrl = card?.image?.detail || card?.image?.thumbnail;
                return (
                    <Box
                        key={card.cardId}
                        onClick={() => imageUrl && setPreviewCard(previewCard?.cardId === card.cardId ? null : card)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            px: 1.25,
                            py: 0.75,
                            borderRadius: '8px',
                            cursor: imageUrl ? 'pointer' : 'default',
                            borderBottom: '1px solid rgba(76, 29, 149, 0.1)',
                            transition: 'background 0.12s ease',
                            '&:last-child': { borderBottom: 'none' },
                            '&:hover': imageUrl ? { bgcolor: 'rgba(124, 58, 237, 0.08)' } : {}
                        }}
                    >
                        <Box sx={{
                            minWidth: 28, height: 22,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: 'rgba(124, 58, 237, 0.18)',
                            border: '1px solid rgba(124, 58, 237, 0.4)',
                            borderRadius: '5px',
                            flexShrink: 0
                        }}>
                            <Typography sx={{ color: '#c4b5fd', fontWeight: 700, fontSize: '0.72rem', lineHeight: 1 }}>
                                {card.quantity || 1}×
                            </Typography>
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '0.82rem', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {card.name}
                            </Typography>
                            {card.subtitle && (
                                <Typography sx={{ color: 'rgba(196,181,253,0.55)', fontSize: '0.7rem', lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {card.subtitle}
                                </Typography>
                            )}
                        </Box>

                        {card.cost != null && (
                            <Typography sx={{ color: 'rgba(196,181,253,0.5)', fontSize: '0.72rem', fontWeight: 600, flexShrink: 0 }}>
                                {card.cost}
                            </Typography>
                        )}
                    </Box>
                );
            })}

            {previewCard && (
                <Box
                    onClick={() => setPreviewCard(null)}
                    sx={{
                        position: 'fixed', inset: 0, zIndex: 1500,
                        backgroundColor: 'rgba(0,0,0,0.88)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        p: 2
                    }}
                >
                    <img
                        src={previewCard?.image?.detail || previewCard?.image?.thumbnail}
                        alt={previewCard?.name}
                        onClick={(e) => e.stopPropagation()}
                        style={{ maxWidth: '88vw', maxHeight: '82vh', borderRadius: 12, boxShadow: '0 8px 48px rgba(0,0,0,0.85)' }}
                    />
                </Box>
            )}
        </Box>
    );
}

function DeckPreviewModal({ open, onClose, deck }) {
    const theme = useTheme();
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [viewMode, setViewMode] = useState('grid');

    if (!deck) return null;

    const handleCopyList = () => { if (deck?.cards) navigator.clipboard.writeText(deck.cards); };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            fullScreen={isSmallMobile}
            PaperProps={{
                sx: {
                    background: 'rgba(13, 17, 30, 0.98)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(76, 29, 149, 0.35)',
                    borderRadius: isSmallMobile ? 0 : '14px',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.7)',
                    maxHeight: isSmallMobile ? '100vh' : '90vh',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderBottom: '1px solid rgba(76, 29, 149, 0.2)', flexShrink: 0 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, fontSize: '0.95rem', lineHeight: 1.2 }} noWrap>
                        {deck.name}
                    </Typography>
                    {deck.archetype && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <InkColorIcons colors={deck.archetype.ink_colors || []} size={14} gap={0.2} />
                            <Typography variant="caption" sx={{ color: 'rgba(196, 181, 253, 0.6)', fontSize: '0.68rem' }} noWrap>
                                {deck.archetype.name}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Box sx={{ display: 'flex', bgcolor: 'rgba(0,0,0,0.3)', borderRadius: '8px', p: '2px', gap: '2px', flexShrink: 0 }}>
                    <Tooltip title="Siatka kart">
                        <IconButton
                            size="small"
                            onClick={() => setViewMode('grid')}
                            sx={{
                                borderRadius: '6px',
                                color: viewMode === 'grid' ? '#c4b5fd' : 'rgba(196,181,253,0.4)',
                                bgcolor: viewMode === 'grid' ? 'rgba(124,58,237,0.25)' : 'transparent',
                                '&:hover': { bgcolor: 'rgba(124,58,237,0.2)' }
                            }}
                        >
                            <GridViewIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Lista kart">
                        <IconButton
                            size="small"
                            onClick={() => setViewMode('list')}
                            sx={{
                                borderRadius: '6px',
                                color: viewMode === 'list' ? '#c4b5fd' : 'rgba(196,181,253,0.4)',
                                bgcolor: viewMode === 'list' ? 'rgba(124,58,237,0.25)' : 'transparent',
                                '&:hover': { bgcolor: 'rgba(124,58,237,0.2)' }
                            }}
                        >
                            <ListIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                    </Tooltip>
                </Box>

                {deck?.cards && (
                    <Tooltip title="Kopiuj listę kart">
                        <IconButton size="small" onClick={handleCopyList} sx={{ color: 'rgba(196,181,253,0.6)', '&:hover': { bgcolor: 'rgba(124,58,237,0.15)' } }}>
                            <ContentCopyIcon sx={{ fontSize: 17 }} />
                        </IconButton>
                    </Tooltip>
                )}
                <IconButton size="small" onClick={onClose} sx={{ color: 'rgba(196,181,253,0.6)', '&:hover': { bgcolor: 'rgba(124,58,237,0.15)' } }}>
                    <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 0, overflowY: 'auto', flex: 1 }}>
                {viewMode === 'grid' ? (
                    <DeckVisual deck={deck} mode="full" viewMode="art-name" grouped={false} />
                ) : (
                    <DeckCardList deck={deck} />
                )}
                {Array.isArray(deck?.deckVisual?.unresolved) && deck.deckVisual.unresolved.length > 0 && (
                    <Box sx={{ m: 2, p: 1.5, backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '10px' }}>
                        <Typography variant="caption" sx={{ color: '#f87171', fontWeight: 700, display: 'block', mb: 0.5 }}>
                            Karty nierozpoznane ({deck.deckVisual.unresolved.length}):
                        </Typography>
                        {deck.deckVisual.unresolved.map((u, i) => (
                            <Typography key={i} variant="caption" sx={{ color: 'rgba(248,113,113,0.85)', display: 'block', fontFamily: 'monospace', fontSize: '0.72rem' }}>
                                {u.quantity}× {u.name}{u.subtitle ? ` - ${u.subtitle}` : ''}
                            </Typography>
                        ))}
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}

export default DeckPreviewModal;
